import { useState, type FormEvent } from "react";
import { useParams, Link } from "react-router-dom";
import { FileCog } from "lucide-react";
import { DetailHeader, BackLink } from "@/components/DetailHeader";
import { PropGrid } from "@/components/PropGrid";
import { SectionCard } from "@/components/SectionCard";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { SpecPhaseBadge } from "@/components/StatusBadges";
import { SpecDialog } from "@/components/dialogs/SpecDialog";
import { AdminInviteDialog } from "@/components/AdminInviteDialog";
import { useProvisionAudit, useSpec, useCell } from "@/api/queries/provisioning";
import { useCustomer } from "@/api/queries/customers";
import { useReissueActivationKey, useRemoveSpec, useRenewAdminInvite, useSetSpecRegistry } from "@/api/mutations/provisioning";
import { relativeTime } from "@/lib/relativeTime";
import type { AdminInviteIssue, AdminInviteState, CustomerSpec } from "@/types";

// #343 (UC-9): Zustand der Erst-Admin-Einladung in Worten. Quelle ist die letzte Meldung des Masters
// (`adminInviteState`); `null` = noch nie gemeldet (älterer Provisioner oder noch nicht ausgerollt).
export function adminInviteLabel(state: AdminInviteState | null | undefined, sealed: boolean): string {
  switch (state) {
    case "open": return "Einladung offen — Admin hat sein Passwort noch nicht gesetzt";
    case "expired": return "Einladung abgelaufen — erneuern";
    case "used": return "Passwort gesetzt";
    case "none": return sealed ? "Einladung ausgerollt, Master hat sie noch nicht angewendet" : "keine Einladung (Admin ohne Einladung angelegt)";
    default: return sealed ? "Einladung versiegelt, noch nicht gemeldet" : "—";
  }
}

function RegistryDialog({ open, onOpenChange, spec }: { open: boolean; onOpenChange: (o: boolean) => void; spec: CustomerSpec }) {
  const set = useSetSpecRegistry();
  const [username, setUsername] = useState(spec.registryUsername ?? "");
  const [token, setToken] = useState("");
  function submit(e: FormEvent) {
    e.preventDefault();
    set.mutate({ id: spec.id, registry: { username: username.trim(), token } }, { onSuccess: () => onOpenChange(false) });
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="registry-dialog">
        <DialogHeader>
          <DialogTitle>Registry-Zugang</DialogTitle>
          <DialogDescription>Pull-Zugang zur privaten Registry — wird an den Schluessel des Zellen-Agenten versiegelt; das Portal kann ihn danach nicht mehr lesen.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2"><Label htmlFor="reg-user">Nutzer</Label><Input id="reg-user" value={username} onChange={(e) => setUsername(e.target.value)} required data-testid="registry-user" /></div>
          <div className="space-y-2"><Label htmlFor="reg-token">Token (write-only)</Label><Input id="reg-token" type="password" value={token} onChange={(e) => setToken(e.target.value)} required data-testid="registry-token" /></div>
          {set.isError && <div className="text-sm text-danger">{set.error.message}</div>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={set.isPending}>Abbrechen</Button>
            <Button type="submit" disabled={set.isPending} data-testid="registry-submit">{set.isPending ? "…" : "Versiegeln"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// #343 UC-8: eine Kundenspezifikation — Soll (Version, Inhalt), Ist (Status aus der Zelle), Geheimnisse (nur die Tatsache),
// Aktionen: aendern (neue Version), Aktivierungsschluessel erneuern, Registry setzen/entfernen, entfernen.
export function SpecDetail() {
  const { id } = useParams<{ id: string }>();
  const spec = useSpec(id);
  const customer = useCustomer(spec.data?.customerId);
  const cell = useCell(spec.data?.cellId);
  const audit = useProvisionAudit({ specId: id, limit: 30 });
  const remove = useRemoveSpec();
  const reissue = useReissueActivationKey();
  const clearRegistry = useSetSpecRegistry();
  const renewInvite = useRenewAdminInvite();
  const [editOpen, setEditOpen] = useState(false);
  // #343 (UC-9): „Einladung erneuern“ = Admin-Zugang zurücksetzen (neues Token, Passwort weg) — Klartext genau einmal.
  const [renewOpen, setRenewOpen] = useState(false);
  const [invite, setInvite] = useState<AdminInviteIssue | null>(null);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [reissueOpen, setReissueOpen] = useState(false);
  const [registryOpen, setRegistryOpen] = useState(false);
  const [clearOpen, setClearOpen] = useState(false);

  if (spec.isLoading) return <div className="max-w-6xl"><Card><CardContent className="p-6" data-testid="detail-loading">Lade…</CardContent></Card></div>;
  if (spec.isError || !spec.data) {
    return (
      <div className="max-w-6xl space-y-4">
        <BackLink to="/customer-specs" label="Spezifikationen" />
        <Card><CardContent className="p-6 text-danger" data-testid="detail-error">Spezifikation nicht gefunden.</CardContent></Card>
      </div>
    );
  }
  const s = spec.data;
  const st = s.lastStatus;

  return (
    <div className="max-w-6xl space-y-5">
      <DetailHeader
        backTo="/customer-specs"
        backLabel="Spezifikationen"
        icon={FileCog}
        title={`${s.customer}@${s.cell}`}
        badge={<span className="flex items-center gap-2"><SpecPhaseBadge phase={st?.phase} removed={s.removed} />{s.secrets.stale && <Badge variant="busy">Geheimnisse erneuern</Badge>}</span>}
        sub={customer.data ? <Link to={`/customers/${s.customerId}`} className="text-accent hover:underline">{customer.data.name}</Link> : <span className="mono text-xs">{s.customerId}</span>}
        actions={
          <>
            <Button variant="outline" onClick={() => setEditOpen(true)} disabled={s.removed} data-testid="edit-spec">Aendern</Button>
            <Button variant="outline" onClick={() => setReissueOpen(true)} disabled={s.removed || !s.licenseId} data-testid="reissue-key">Aktivierungsschluessel erneuern</Button>
            <Button variant="outline" onClick={() => setRenewOpen(true)} disabled={s.removed || !s.adminEmail} data-testid="renew-admin-invite">Einladung erneuern</Button>
            <Button variant="outline" onClick={() => setRegistryOpen(true)} disabled={s.removed} data-testid="set-registry">Registry</Button>
            <Button variant="destructive" onClick={() => setRemoveOpen(true)} disabled={s.removed} data-testid="remove-spec">Entfernen</Button>
          </>
        }
      />
      <div className="grid gap-5 lg:grid-cols-[1fr_340px]">
        <div className="space-y-5">
          <SectionCard title="Ist-Zustand" description="Gemeldet vom Agenten der Zelle">
            {!st ? (
              <div className="text-sm text-text3" data-testid="status-empty">Noch keine Statusmeldung — der Agent holt Auftraege im Minutentakt.</div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2 text-sm">
                <div><div className="text-[11.5px] font-semibold text-text3">Phase</div><SpecPhaseBadge phase={st.phase} /></div>
                <div><div className="text-[11.5px] font-semibold text-text3">Ausgerollte Auftrags-Version</div><div className="tnum">v{st.portalVersion}{st.portalVersion !== s.version && <span className="ml-2 text-warn">(Soll v{s.version})</span>}</div></div>
                <div><div className="text-[11.5px] font-semibold text-text3">Image</div><div className="mono text-xs">{st.imageTag ?? "—"}{st.chartVersion ? ` · Chart ${st.chartVersion}` : ""}</div></div>
                <div><div className="text-[11.5px] font-semibold text-text3">Gemeldet</div><div>{s.lastStatusAt ? relativeTime(s.lastStatusAt) : "—"}{st.attempts ? ` · Versuch ${st.attempts}` : ""}{st.permanent ? " · dauerhaft" : ""}</div></div>
                <div className="sm:col-span-2"><div className="text-[11.5px] font-semibold text-text3">Meldung</div><div className={st.phase === "failed" ? "text-danger" : "text-text"} data-testid="status-message">{st.message || "—"}</div></div>
                {s.adminEmail && (
                  <div className="sm:col-span-2"><div className="text-[11.5px] font-semibold text-text3">Erst-Admin ({s.adminEmail})</div><div className={s.adminInviteState === "expired" ? "text-warn" : "text-text"} data-testid="status-admin-invite">{adminInviteLabel(s.adminInviteState, !!s.secrets.adminInvite)}</div></div>
                )}
              </div>
            )}
          </SectionCard>
          <SectionCard title="Chart-Werte" description="values (Allowlist) — JSON">
            <pre className="max-h-64 overflow-auto rounded bg-surface2 p-3 font-mono text-[11px] text-text2" data-testid="spec-values-view">{JSON.stringify(s.values, null, 2)}</pre>
            {s.channels.length > 0 && <div className="mt-3 text-sm text-text2">Kanaele: {s.channels.map((c) => `${c.name} (${c.type})`).join(", ")}</div>}
          </SectionCard>
          <SectionCard title="Verlauf" description="Auftraege, Geheimnisse, Statuswechsel" noPad>
            {(audit.data ?? []).length === 0 ? (
              <div className="p-6 text-center text-sm text-text3">Noch keine Eintraege.</div>
            ) : (
              <Table>
                <TableHeader><TableRow><TableHead>Zeit</TableHead><TableHead>Aktion</TableHead><TableHead>Akteur</TableHead><TableHead>Details</TableHead></TableRow></TableHeader>
                <TableBody>
                  {(audit.data ?? []).map((e) => (
                    <TableRow key={e.id}>
                      <TableCell className="text-text3">{relativeTime(e.ts)}</TableCell>
                      <TableCell className="mono text-xs">{e.action}</TableCell>
                      <TableCell className="text-text2">{e.actor}</TableCell>
                      <TableCell className="mono text-[11px] text-text3">{JSON.stringify(e.details)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </SectionCard>
        </div>
        <PropGrid rows={[
          ["Spezifikations-ID", <span className="mono text-xs">{s.id}</span>],
          ["Soll-Version", <span className="tnum">v{s.version}</span>],
          ["Zelle", cell.data ? <Link to={`/cells/${s.cellId}`} className="text-accent hover:underline">{cell.data.name}</Link> : s.cell],
          ["Plan", s.plan.toUpperCase()],
          ["Image", <span className="mono text-xs">{s.imageTag}</span>],
          ["Chart-Pin", s.chartVersion ? `${s.chartVersion}${s.chartChecksum ? " · geprueft" : ""}` : "—"],
          ["Host", s.host ? <a href={`https://${s.host}/ui`} target="_blank" rel="noreferrer" className="text-accent hover:underline" data-testid="spec-host-link">{s.host}</a> : "—"],
          ["Relay", s.features.relay ? "eigener" : "der Zelle"],
          ["Updates", `${s.updates.mode} · ${s.updates.channel}${s.updates.window ? ` · ${s.updates.window}` : ""}`],
          ["Erst-Admin", s.adminEmail ?? "—"],
          ["Einladung", s.adminEmail ? (s.secrets.adminInvite ? "versiegelt" : s.secrets.stale ? "ungueltig (Re-Enroll)" : "keine") : "—"],
          ["LLM-Gateway", s.features.litellm ? "eigenes (LiteLLM + Datenbank)" : "—"],
          ["Lizenz", s.licenseId ? <Link to={`/licenses/${s.licenseId}`} className="mono text-xs text-accent hover:underline">{s.licenseId}</Link> : "—"],
          ["Aktivierungsschluessel", s.secrets.activationKey ? "versiegelt" : s.secrets.stale ? "ungueltig (Re-Enroll)" : "keiner"],
          ["Registry", s.secrets.registry ? `versiegelt (${s.registryUsername})` : s.secrets.stale && s.registryUsername ? `ungueltig (${s.registryUsername})` : "keine"],
          ["Angelegt", relativeTime(s.createdAt)],
          ["Geaendert", relativeTime(s.updatedAt)],
        ]} />
      </div>
      {editOpen && <SpecDialog open={editOpen} onOpenChange={setEditOpen} existing={s} />}
      {removeOpen && (
        <ConfirmDialog open={removeOpen} onOpenChange={setRemoveOpen} title="Kunden entfernen" description="Der Agent der Zelle deinstalliert das Release. Namespaces, Geheimnisse und Volumes bleiben (Purge nur von Hand). Zum Bestaetigen den Kundennamen eintippen." confirmText="Entfernen" confirmVariant="destructive" requiredTypeText={s.customer} isPending={remove.isPending} errorMessage={remove.isError ? remove.error.message : undefined} onConfirm={() => remove.mutate(s.id, { onSuccess: () => setRemoveOpen(false) })} />
      )}
      {reissueOpen && (
        <ConfirmDialog open={reissueOpen} onOpenChange={setReissueOpen} title="Aktivierungsschluessel erneuern" description="Erzeugt einen neuen Einmal-Schluessel der Lizenz und versiegelt ihn an den Agenten der Zelle (neue Auftrags-Version). Der alte bleibt im Lizenz-Register." confirmText="Erneuern" isPending={reissue.isPending} errorMessage={reissue.isError ? reissue.error.message : undefined} onConfirm={() => reissue.mutate({ id: s.id }, { onSuccess: () => setReissueOpen(false) })} />
      )}
      {renewOpen && (
        <ConfirmDialog open={renewOpen} onOpenChange={setRenewOpen} title="Einladung erneuern" description={`Erzeugt eine neue Einladung für ${s.adminEmail} und versiegelt sie an den Agenten der Zelle (neue Auftrags-Version). Beim Ausrollen setzt der Master den Zugang dieses Admins zurück: ein bestehendes Passwort gilt nicht mehr, der Admin setzt über den neuen Link ein neues. Der Link wird genau einmal angezeigt.`} confirmText="Erneuern" isPending={renewInvite.isPending} errorMessage={renewInvite.isError ? renewInvite.error.message : undefined} onConfirm={() => renewInvite.mutate(s.id, { onSuccess: (r) => { setRenewOpen(false); setInvite(r.adminInvite); } })} />
      )}
      {/* reset(): der Klartext darf nach dem Schliessen nicht im Mutations-Cache weiterleben (Review LOW-8) */}
      <AdminInviteDialog invite={invite} onClose={() => { setInvite(null); renewInvite.reset(); }} />
      {registryOpen && (s.secrets.registry ? (
        <ConfirmDialog open={registryOpen} onOpenChange={setRegistryOpen} title="Registry-Zugang" description="Bestehenden Zugang entfernen (neue Auftrags-Version) — oder unten einen neuen Zugang setzen (ersetzt den alten)." confirmText="Zugang entfernen" confirmVariant="destructive" isPending={clearRegistry.isPending} errorMessage={clearRegistry.isError ? clearRegistry.error.message : undefined} onConfirm={() => clearRegistry.mutate({ id: s.id, registry: null }, { onSuccess: () => { setRegistryOpen(false); setClearOpen(false); } })}>
          <Button variant="outline" size="sm" onClick={() => { setRegistryOpen(false); setClearOpen(true); }} data-testid="registry-replace">Stattdessen neuen Zugang setzen</Button>
        </ConfirmDialog>
      ) : (
        <RegistryDialog open={registryOpen} onOpenChange={setRegistryOpen} spec={s} />
      ))}
      {clearOpen && <RegistryDialog open={clearOpen} onOpenChange={setClearOpen} spec={s} />}
    </div>
  );
}
